import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Col,
  Input,
  notification,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
} from "antd";

import { PlusOutlined } from "@ant-design/icons";

import BasicButtonPrimary from "components/BasicButtonPrimary";
import BasicTablePaging from "components/BasicTablePaging/BasicTablePaging";

import {
  VERY_BIG_COLUMN_WIDTH,
  BIG_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  NORMAL_COLUMN_WIDTH,
} from "constants/app";
import ModalAddCamera from "./modalAddCamera";
import CameraService from "services/cameraService";
import { CAMERA_OPTIONS_STATUS } from "constants/camera";
import { CAMERA_RESOLUTION_OPTIONS } from "constants/camera";
import ModalDetailCamera from "./modalDetailCamera";

const ListCamera = () => {
  // Hooks
  const { t: translation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const defaultFilter = {
    filter: {},
    skip: 0,
    limit: 20,
    searchText: undefined,
    order: {
      key: "createdAt",
      value: "desc",
    },
  };

  // State
  const [dataFilter, setDataFilter] = useState(defaultFilter);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [data, setData] = useState([]);
  const [currentStationCameraId, setCurrentStationCameraId] = useState(null);

  const handleDeleteCamera = (id) => {
    CameraService.Delete({ id })
      .then(() => {
        notification["success"]({
          message: "",
          description: translation("camera.deleteSuccess"),
        });
        // Refresh data after deletion
        setData((prevData) =>
          prevData.filter((item) => item.stationCameraId !== id)
        );
        setDataFilter(defaultFilter);
        handleFetchDataCamera(dataFilter);
      })
      .catch(() => {
        notification["error"]({
          message: "",
          description: translation("camera.deleteFailed"),
        });
      });
  };

  const handleFetchDataCamera = (dataFilter) => {
    setLoading(true);
    CameraService.getList(dataFilter)
      .then((result) => {
        const { data } = result;
        setData(data);
      })
      .catch(() => {
        notification["error"]({
          message: "",
          description: translation("camera.error"),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOpenDetailCamera = (id) => {
    setCurrentStationCameraId(id);
    setIsOpenDetailModal(true);
  };

  // Constant
  const columns = [
    {
      title: translation("camera.index"),
      key: "index",
      dataIndex: "index",
      width: MIN_COLUMN_WIDTH,
      align: "center",
      render: (_, __, index) => {
        return (
          <div className="d-flex justify-content-center aligns-items-center">
            {dataFilter.skip ? dataFilter.skip + index + 1 : index + 1}
          </div>
        );
      },
    },
    {
      title: translation("camera.name"),
      key: "name",
      dataIndex: "name",
      align: "center",
      width: VERY_BIG_COLUMN_WIDTH,
    },
    {
      title: translation("camera.brand"),
      key: "brand",
      dataIndex: "brand",
      align: "center",
      width: BIG_COLUMN_WIDTH,
    },
    {
      title: translation("camera.model"),
      key: "model",
      dataIndex: "model",
      align: "center",
      width: VERY_BIG_COLUMN_WIDTH,
    },
    {
      title: translation("camera.ip&host"),
      key: "stationCameraConnections",
      dataIndex: "stationCameraConnections",
      align: "center",
      width: VERY_BIG_COLUMN_WIDTH,
      render: (value) => {
        return (
          <div>
            {value.map((item, index) => (
              <div key={index}>{item.ipAddress}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: translation("camera.status"),
      key: "status",
      dataIndex: "status",
      align: "center",
      width: VERY_BIG_COLUMN_WIDTH,
      render: (value) => {
        const isOnline = value === CAMERA_OPTIONS_STATUS[0].value; // Assuming the first option is "Online"
        return (
          <div className={`${isOnline ? "text-success" : "text-danger"}`}>
            {isOnline
              ? translation("camera.online")
              : translation("camera.offline")}
          </div>
        );
      },
    },
    {
      title: translation("camera.resolution"),
      key: "resolution",
      dataIndex: "resolution",
      align: "center",
      width: NORMAL_COLUMN_WIDTH,
      render: (value, record) => {
        return <div>{value}</div>;
      },
    },
    {
      title: translation("camera.actions"),
      key: "action",
      align: "center",
      width: VERY_BIG_COLUMN_WIDTH,
      render: (value, record) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => {
                handleOpenDetailCamera(record.stationCameraId);
              }}
            >
              {translation("camera.detail")}
            </Button>
            <Popconfirm
              title={translation("camera.deleteConfirm")}
              onConfirm={() => {
                handleDeleteCamera(record.stationCameraId);
              }}
              okText={translation("camera.yes")}
              cancelText={translation("camera.no")}
            >
              <Button type="link">{translation("camera.delete")}</Button>
            </Popconfirm>
          </>
        );
      },
    },
  ];

  // Functions
  const handleChangePage = (pageNum) => {
    const newFilter = {
      ...dataFilter,
      skip: (pageNum - 1) * dataFilter.limit,
    };
    setDataFilter(newFilter);
    handleFetchDataCamera(newFilter);
  };

  useEffect(() => {
    handleFetchDataCamera(dataFilter);
  }, []);

  return (
    <div>
      <Row wrap gutter={[24,24]} className="camera-action mb-3">
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Input.Search
            autoFocus
            placeholder={translation("camera.search")}
            className="w-100"
            onSearch={(value) => {
              const newFilter = {
                ...dataFilter,
                skip: 0,
                searchText: value ? value : undefined,
              };
              setDataFilter(newFilter);
              handleFetchDataCamera(newFilter);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Select
            className="w-100"
            placeholder={translation("camera.cameraStatus")}
            // defaultValue={"all"} // giá trị khớp với Option value
            value={dataFilter.filter.status || "all"}
            onChange={(value) => {
              const newFilter = {
                ...dataFilter,
                filter: {
                  ...dataFilter.filter,
                  status: value === "all" ? undefined : value,
                },
                skip: 0,
              };
              setDataFilter(newFilter);
              handleFetchDataCamera(newFilter);
            }}
          >
            <Select.Option key="all" value={"all"}>
              Tất cả trạng thái
            </Select.Option>
            {CAMERA_OPTIONS_STATUS.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <Select
            className="w-100"
            placeholder={translation("camera.resolution")}
            value={dataFilter.filter.resolution || "all"}
            onChange={(value) => {
              const newFilter = {
                ...dataFilter,
                filter: {
                  ...dataFilter.filter,
                  resolution: value === "all" ? undefined : value,
                },
                skip: 0,
              };
              setDataFilter(newFilter);
              handleFetchDataCamera(newFilter);
            }}
          >
            <Select.Option key="all" value={"all"}>
              Tất cả độ phân giải
            </Select.Option>
            {CAMERA_RESOLUTION_OPTIONS.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={4}>
          <BasicButtonPrimary
            className="d-flex align-items-center justify-content-center"
            icon={<PlusOutlined />}
            text={translation("camera.cameraAdd")}
            onClick={() => setIsOpenCreateModal(true)}
          />
        </Col>
      </Row>
      <div>
        {loading ? (
          <Spin />
        ) : (
          <>
            <Table
              dataSource={data}
              columns={columns}
              scroll={{ x: 1600 }}
              pagination={false}
            />
            <BasicTablePaging
              handlePaginations={handleChangePage}
              skip={dataFilter.skip}
              count={
                data?.length < dataFilter?.limit && data?.length === 0
                  ? true
                  : false
              }
              currentPage={
                dataFilter.skip ? dataFilter.skip / dataFilter.limit + 1 : 1
              }
            ></BasicTablePaging>
          </>
        )}
      </div>

      <ModalAddCamera
        isOpen={isOpenCreateModal}
        setIsOpen={setIsOpenCreateModal}
        setData={setData}
        resetFilter={() => {
          setDataFilter(defaultFilter);
          handleFetchDataCamera(defaultFilter);
        }}
      />
      {isOpenDetailModal && (
        <ModalDetailCamera
          isOpen={isOpenDetailModal}
          setIsOpen={setIsOpenDetailModal}
          id={currentStationCameraId}
          setData={setData}
          resetFilter={() => {
            setDataFilter(defaultFilter);
            handleFetchDataCamera(defaultFilter);
          }}
        />
      )}
    </div>
  );
};

export default ListCamera;
